import {
    Alert,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
} from "@mui/material"
import { useEffect, useState } from "react"
import { CancelButton } from "../../../app/components/CancelButton"
import { SaveButton } from "../../../app/components/SaveButton"
import { useAppDispatch } from "../../../app/hooks"
import { DocumentWorkflowStageTypeEnum } from "../../documents/workflow/documentWorkflowApi"
import { showError, showSuccess } from "../../notifications/notificationsSlice"
import {
    addCompanyWorkflowDefinitionStage,
    DocumentWorkflowDefinitionDetailsDto,
    DocumentWorkflowDefinitionStageDetailsDto,
    updateCompanyWorkflowDefinitionStage,
} from "./companyDocumentWorkflowSlice"

interface AddEditCompanyWorkflowStageDialogProps {
    open: boolean
    mode: "add" | "edit"
    definition: DocumentWorkflowDefinitionDetailsDto | null
    stageType?: DocumentWorkflowStageTypeEnum
    stage?: DocumentWorkflowDefinitionStageDetailsDto | null
    onClose: () => void
    onSaved: (definition: DocumentWorkflowDefinitionDetailsDto) => void
}

export const AddEditCompanyWorkflowStageDialog = ({
    open,
    mode,
    definition,
    stageType = "REVIEW",
    stage = null,
    onClose,
    onSaved,
}: AddEditCompanyWorkflowStageDialogProps) => {
    const dispatch = useAppDispatch()
    const isEditMode = mode === "edit"
    const [isSaving, setIsSaving] = useState(false)
    const [stageName, setStageName] = useState("")
    const [stageDescription, setStageDescription] = useState("")
    const [stageDeadlineDays, setStageDeadlineDays] = useState("")
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (!open) {
            return
        }

        if (isEditMode && stage) {
            setStageName(stage.name)
            setStageDescription(stage.description || "")
            setStageDeadlineDays(stage.defaultDeadlineDays === null ? "" : String(stage.defaultDeadlineDays))
            setError(null)
            return
        }

        setStageName(stageType === "REVIEW" ? "Review" : "Approval")
        setStageDescription("")
        setStageDeadlineDays("")
        setError(null)
    }, [open, isEditMode, stage, stageType])

    const normalizeWorkflowStageType = (value: unknown): DocumentWorkflowStageTypeEnum => {
        if (value === "EDIT" || value === "REVIEW" || value === "APPROVAL") {
            return value
        }

        return "REVIEW"
    }

    const buildStageServerError = (payload: unknown, fallback: string) => {
        if (!payload || typeof payload !== "object") {
            return fallback
        }

        const apiPayload = payload as {
            kind?: string
            problem?: {
                title?: string
                detail?: string
                errors?: Record<string, unknown>
                problems?: unknown[]
            }
        }

        if (apiPayload.kind !== "http" || !apiPayload.problem) {
            return fallback
        }

        const problem = apiPayload.problem
        const baseMessage = [problem.title, problem.detail].filter(Boolean).join(": ")
        const validationMessages: string[] = []

        if (problem.errors && typeof problem.errors === "object") {
            Object.entries(problem.errors).forEach(([field, value]) => {
                if (Array.isArray(value)) {
                    value.forEach(item => {
                        if (typeof item === "string" && item.trim()) {
                            validationMessages.push(`${field}: ${item}`)
                        }
                    })
                } else if (typeof value === "string" && value.trim()) {
                    validationMessages.push(`${field}: ${value}`)
                }
            })
        }

        if (problem.problems && Array.isArray(problem.problems)) {
            problem.problems.forEach(item => {
                if (typeof item === "string" && item.trim()) {
                    validationMessages.push(item)
                }
            })
        }

        if (validationMessages.length === 0) {
            return baseMessage || fallback
        }

        return `${baseMessage || fallback}\n${validationMessages.join("\n")}`
    }

    const handleSave = async () => {
        if (!definition) {
            setError("Nie udało się odczytać definicji workflow.")
            return
        }

        const trimmedName = stageName.trim()
        if (!trimmedName) {
            setError("Nazwa etapu jest wymagana.")
            return
        }

        const parsedDeadline = stageDeadlineDays.trim() === ""
            ? null
            : Number(stageDeadlineDays)

        if (parsedDeadline !== null && (!Number.isInteger(parsedDeadline) || parsedDeadline < 0)) {
            setError("Deadline musi być pusty albo liczbą całkowitą >= 0.")
            return
        }

        setIsSaving(true)
        setError(null)

        try {
            if (!isEditMode) {
                const nextOrder = definition.stages.length > 0
                    ? Math.max(...definition.stages.map(item => item.order)) + 1
                    : 1

                const result = await dispatch(addCompanyWorkflowDefinitionStage({
                    definitionId: definition.id,
                    versionId: definition.versionId,
                    dto: {
                        name: trimmedName,
                        description: stageDescription.trim() || null,
                        type: stageType,
                        order: nextOrder,
                        defaultDeadlineDays: parsedDeadline,
                    },
                }))

                if (addCompanyWorkflowDefinitionStage.fulfilled.match(result)) {
                    dispatch(showSuccess("saved"))
                    onSaved(result.payload)
                    return
                }

                setError(buildStageServerError(result.payload, "Nie udało się dodać etapu workflow."))
                return
            }

            if (!stage) {
                setError("Nie udało się odczytać etapu workflow.")
                return
            }

            const result = await dispatch(updateCompanyWorkflowDefinitionStage({
                definitionId: definition.id,
                versionId: definition.versionId,
                stageId: stage.id,
                dto: {
                    name: trimmedName,
                    description: stageDescription.trim() || null,
                    type: normalizeWorkflowStageType(stage.type),
                    order: stage.order,
                    defaultDeadlineDays: parsedDeadline,
                },
            }))

            if (updateCompanyWorkflowDefinitionStage.fulfilled.match(result)) {
                dispatch(showSuccess("saved"))
                onSaved(result.payload)
                return
            }

            setError(buildStageServerError(result.payload, "Nie udało się zapisać etapu workflow."))
        } catch {
            dispatch(showError("error"))
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <Dialog open={open} fullWidth maxWidth="sm" onClose={onClose}>
            <DialogTitle>{isEditMode ? "Edycja etapu" : "Nowy etap"}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 2 }}>
                    <TextField
                        required
                        label="Nazwa etapu"
                        value={stageName}
                        onChange={event => setStageName(event.target.value)}
                        fullWidth
                        size="small"
                    />

                    <TextField
                        label="Opis etapu"
                        value={stageDescription}
                        onChange={event => setStageDescription(event.target.value)}
                        fullWidth
                        size="small"
                        multiline
                        minRows={3}
                    />

                    <TextField
                        label="Domyślny deadline [dni]"
                        value={stageDeadlineDays}
                        onChange={event => setStageDeadlineDays(event.target.value)}
                        fullWidth
                        size="small"
                        type="number"
                        inputProps={{ min: 0 }}
                    />

                    {error && (
                        <Alert severity="error" sx={{ whiteSpace: "pre-line" }}>
                            {error}
                        </Alert>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <SaveButton
                    loading={isSaving}
                    disabled={!stageName.trim()}
                    onClick={handleSave}
                />
                <CancelButton loading={false} onClick={onClose} />
            </DialogActions>
        </Dialog>
    )
}
